import { useEffect, useState } from "react";
import { Loader2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { PHONE_DISPLAY } from "./HomeTopBar";

const PRIVACY_URL = "https://www.sima.madrid/politica-de-privacidad-y-cookies";

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  message: "",
  privacy_accepted: false,
  // Honeypot: hidden from people, bots fill it in and the backend drops the
  // request without saying so.
  website: "",
};

/** «Te llamamos»: the visitor leaves their details and staff call them back. */
export default function ContactRequestDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accepted) {
      toast.error("Debes aceptar la política de privacidad.");
      return;
    }
    setSending(true);
    try {
      await api.post("/public/contact-requests", {
        ...form,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      toast.success("¡Recibido! Te llamaremos lo antes posible.");
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err.response
          ? formatApiErrorDetail(err.response.data?.detail)
          : `No se pudo enviar. Llámanos al ${PHONE_DISPLAY}.`,
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-6 sm:p-7 max-h-[92vh] overflow-y-auto" data-testid="contact-modal">
        <DialogHeader className="flex-row items-center gap-3.5 space-y-0 text-left">
          <span className="shrink-0 w-12 h-12 rounded-[10px] bg-brand-soft text-brand flex items-center justify-center">
            <PhoneCall className="w-6 h-6" />
          </span>
          <div className="space-y-1">
            <DialogTitle className="font-display text-[1.75rem] leading-tight text-brand-ink">Te llamamos</DialogTitle>
            <DialogDescription>Déjanos tus datos y te llamamos para resolver tus dudas.</DialogDescription>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3.5">
            <div className="space-y-1.5">
              <Label htmlFor="contact-first-name">Nombre</Label>
              <Input id="contact-first-name" required maxLength={80} autoComplete="given-name" placeholder="Tu nombre" {...field("first_name")} data-testid="contact-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-last-name">Apellidos</Label>
              <Input id="contact-last-name" required maxLength={120} autoComplete="family-name" placeholder="Tus apellidos" {...field("last_name")} data-testid="contact-last-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">Email</Label>
              <Input id="contact-email" type="email" required maxLength={254} autoComplete="email" placeholder="nombre@correo.com" {...field("email")} data-testid="contact-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">Teléfono</Label>
              <Input id="contact-phone" type="tel" required minLength={6} maxLength={30} autoComplete="tel" placeholder="600 000 000" {...field("phone")} data-testid="contact-phone" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contact-message">Tu consulta</Label>
            <Textarea id="contact-message" rows={4} maxLength={2000} placeholder="Escribe aquí tu consulta" {...field("message")} data-testid="contact-message" />
          </div>

          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute -left-[9999px] w-px h-px opacity-0"
            {...field("website")}
          />

          <label className="flex items-start gap-2.5 text-[13px] leading-snug text-gray-600 cursor-pointer">
            <Checkbox
              checked={form.privacy_accepted}
              onCheckedChange={(v) => setForm((f) => ({ ...f, privacy_accepted: !!v }))}
              className="mt-0.5"
              data-testid="contact-privacy"
            />
            <span>
              He leído y acepto la{" "}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-ink underline hover:text-brand"
              >
                política de privacidad
              </a>
              .
            </span>
          </label>

          <Button
            type="submit"
            disabled={sending}
            className="w-full h-11 bg-brand hover:bg-brand-hover text-white text-[15px] font-semibold"
            data-testid="contact-submit"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PhoneCall className="w-4 h-4 mr-2" />}
            Te llamamos
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
